import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analytics, { initGA4, getTrafficSource } from '@/utils/analytics';

/**
 * Hook to automatically track page views and provide analytics functions
 */
export const useAnalytics = () => {
  const location = useLocation();
  const prevPath = useRef(location.pathname);
  const initialized = useRef(false);

  // Initialize GA4 once
  useEffect(() => {
    if (!initialized.current) {
      initGA4();
      
      // Track initial traffic source
      const source = getTrafficSource();
      analytics.funnelStep('landing', 1, { 
        traffic_source: source.type,
        traffic_source_detail: source.source,
        landing_page: location.pathname
      });
      
      initialized.current = true;
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      // Get page title from document
      const title = document.title;
      
      // Track page view
      analytics.pageView(location.pathname, title);
      
      // Track resource page views specifically
      if (location.pathname.startsWith('/resources/')) {
        const slug = location.pathname.replace('/resources/', '');
        analytics.resourcePageView(slug, 'article', null, null);
      } else if (location.pathname === '/resources') {
        analytics.resourcePageView('index', 'index', null, null);
      }
      
      // Track funnel navigation
      trackFunnelNavigation(prevPath.current, location.pathname);
      
      prevPath.current = location.pathname;
    }
  }, [location.pathname]);

  return analytics;
};

/**
 * Track navigation through the conversion funnel
 */
const trackFunnelNavigation = (fromPath, toPath) => {
  // Resource funnel: Landing → Resources → Brands/Creators
  if (fromPath === '/' && toPath.startsWith('/resources')) {
    analytics.funnelStep('landing_to_resources', 2, { from: fromPath, to: toPath });
  }
  
  if (fromPath.startsWith('/resources') && (toPath === '/brands/discover' || toPath === '/explore-brands')) {
    analytics.funnelStep('resources_to_brands', 3, { from: fromPath, to: toPath });
  }
  
  if (fromPath.startsWith('/resources') && toPath === '/leaderboards') {
    analytics.funnelStep('resources_to_leaderboards', 3, { from: fromPath, to: toPath });
  }
  
  // Direct discovery funnel
  if (fromPath === '/' && toPath === '/brands/discover') {
    analytics.funnelStep('landing_to_discover', 2, { from: fromPath, to: toPath });
  }
  
  if (fromPath === '/' && toPath === '/explore-brands') {
    analytics.funnelStep('landing_to_explore_brands', 2, { from: fromPath, to: toPath });
  }
  
  // Engagement funnel
  if ((fromPath === '/brands/discover' || fromPath === '/explore-brands') && toPath.includes('/creator/')) {
    analytics.funnelStep('brands_to_creator_profile', 4, { from: fromPath, to: toPath });
  }
};

/**
 * Hook to track first interaction
 */
export const useFirstInteraction = () => {
  useEffect(() => {
    const handleFirstInteraction = (e) => {
      const target = e.target;
      const tagName = target.tagName?.toLowerCase() || 'unknown';
      
      let interactionType = 'unknown';
      let element = '';
      
      // Safely get className (handle SVG elements which have SVGAnimatedString)
      const getClassName = (el) => {
        if (!el || !el.className) return '';
        if (typeof el.className === 'string') return el.className;
        if (el.className.baseVal) return el.className.baseVal; // SVGAnimatedString
        return '';
      };
      
      // Safely get text content
      const getTextContent = (el) => {
        if (!el) return '';
        const text = el.textContent || el.innerText || '';
        return text.slice(0, 50).trim();
      };
      
      if (tagName === 'button' || target.closest('button')) {
        interactionType = 'button_click';
        const btn = target.closest('button') || target;
        element = getTextContent(btn) || getClassName(btn).slice(0, 50);
      } else if (tagName === 'a' || target.closest('a')) {
        interactionType = 'link_click';
        const link = target.closest('a') || target;
        element = link.href || getTextContent(link);
      } else if (tagName === 'input') {
        interactionType = 'input_focus';
        element = target.placeholder || target.name || 'input';
      } else {
        interactionType = 'click';
        const className = getClassName(target);
        element = className ? className.slice(0, 50) : tagName;
      }
      
      analytics.firstInteraction(interactionType, element);
      
      // Remove listener after first interaction
      document.removeEventListener('click', handleFirstInteraction);
    };
    
    // Only add if not already tracked this session
    if (!sessionStorage.getItem('first_interaction_tracked')) {
      document.addEventListener('click', handleFirstInteraction);
    }
    
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
    };
  }, []);
};

export default useAnalytics;
